import React, { useState, useMemo } from 'react';

import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

// Calculation constants per user requirement
const WORKING_DAYS_PER_MONTH = 30;
const WORKING_HOURS_PER_DAY = 9;
const DEFAULT_SPF_PERCENTAGE = 22.5;

const calcDailySalary = (monthly) => {
  const m = parseFloat(monthly) || 0;
  return m > 0 ? m / WORKING_DAYS_PER_MONTH : 0;
};

const calcHourlySalary = (daily) => {
  return daily > 0 ? daily / WORKING_HOURS_PER_DAY : 0;
};

const calcSPFValue = (monthlySalary, spfPercentage, nationality) => {
  if (nationality !== 'omani') return 0;
  const salary = parseFloat(monthlySalary) || 0;
  const pct = parseFloat(spfPercentage) || DEFAULT_SPF_PERCENTAGE;
  return salary * (pct / 100);
};

const EngineersGrid = ({ data, onUpdate, onDelete, searchQuery }) => {
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);

  const roleOptions = [
    { value: 'senior', label: 'Senior Engineer' },
    { value: 'mid', label: 'Mid-Level Engineer' },
    { value: 'junior', label: 'Junior Engineer' },
    { value: 'lead', label: 'Lead Engineer' },
    { value: 'architect', label: 'Solution Architect' }
  ];

  const nationalityOptions = [
    { value: 'omani', label: 'Omani' },
    { value: 'expat', label: 'Expat' },
  ];

  const filteredData = useMemo(() => {
    if (!searchQuery) return data;
    const query = searchQuery?.toLowerCase();
    return data?.filter(item =>
      item?.name?.toLowerCase()?.includes(query) ||
      item?.role?.toLowerCase()?.includes(query) ||
      item?.specialization?.toLowerCase()?.includes(query)
    );
  }, [data, searchQuery]);

  const handleEdit = (item) => {
    setEditingId(item?.id);
    setEditData({
      ...item,
      nationality: item?.nationality ?? 'expat',
      spfPercentage: item?.spfPercentage ?? DEFAULT_SPF_PERCENTAGE,
      healthInsuranceEnabled: item?.healthInsuranceEnabled ?? false,
      healthInsuranceValue: item?.healthInsuranceValue ?? 0,
    });
  };

  const handleSave = () => {
    const monthly = parseFloat(editData?.monthlySalary) || 0;
    const daily = calcDailySalary(monthly);
    const hourly = calcHourlySalary(daily);
    const nationality = editData?.nationality || 'expat';
    const spfPercentage = parseFloat(editData?.spfPercentage) || DEFAULT_SPF_PERCENTAGE;
    const spfValue = calcSPFValue(monthly, spfPercentage, nationality);
    onUpdate({
      ...editData,
      monthlySalary: monthly,
      dailySalary: daily,
      hourlySalary: hourly,
      internalCost: daily,
      nationality,
      spfPercentage,
      spfValue,
      healthInsuranceEnabled: editData?.healthInsuranceEnabled ?? false,
      healthInsuranceValue: parseFloat(editData?.healthInsuranceValue) || 0,
    });
    setEditingId(null);
    setEditData({});
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditData({});
  };

  const handleMonthlySalaryChange = (value) => {
    const monthly = parseFloat(value) || 0;
    const daily = calcDailySalary(monthly);
    const hourly = calcHourlySalary(daily);
    const spfPercentage = parseFloat(editData?.spfPercentage) || DEFAULT_SPF_PERCENTAGE;
    const nationality = editData?.nationality || 'expat';
    const spfValue = calcSPFValue(monthly, spfPercentage, nationality);
    setEditData(prev => ({
      ...prev,
      monthlySalary: value,
      dailySalary: daily,
      hourlySalary: hourly,
      internalCost: daily,
      spfValue,
    }));
  };

  const handleNationalityChange = (value) => {
    const monthly = parseFloat(editData?.monthlySalary) || 0;
    const spfPercentage = parseFloat(editData?.spfPercentage) || DEFAULT_SPF_PERCENTAGE;
    const spfValue = calcSPFValue(monthly, spfPercentage, value);
    setEditData(prev => ({ ...prev, nationality: value, spfValue }));
  };

  const handleSPFPercentageChange = (value) => {
    const monthly = parseFloat(editData?.monthlySalary) || 0;
    const nationality = editData?.nationality || 'expat';
    const spfValue = calcSPFValue(monthly, value, nationality);
    setEditData(prev => ({ ...prev, spfPercentage: value, spfValue }));
  };

  const handleSelectRow = (id) => {
    setSelectedRows(prev =>
      prev?.includes(id) ? prev?.filter(rowId => rowId !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedRows?.length === filteredData?.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(filteredData?.map(item => item?.id));
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'OMR',
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    })?.format(value || 0);
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse min-w-[1800px]">
        <thead>
          <tr className="bg-muted border-b border-border">
            <th className="w-12 px-4 py-3 text-left">
              <input
                type="checkbox"
                checked={selectedRows?.length === filteredData?.length && filteredData?.length > 0}
                onChange={handleSelectAll}
                className="w-4 h-4 rounded border-border"
              />
            </th>
            <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Name</th>
            <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Role</th>
            <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Specialization</th>
            <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Nationality</th>
            <th className="px-4 py-3 text-right text-sm font-semibold text-foreground">Monthly Salary</th>
            <th className="px-4 py-3 text-right text-sm font-semibold text-foreground">Daily Salary <span className="text-xs font-normal text-muted-foreground">(÷30)</span></th>
            <th className="px-4 py-3 text-right text-sm font-semibold text-foreground">Hourly Salary <span className="text-xs font-normal text-muted-foreground">(÷9)</span></th>
            <th className="px-4 py-3 text-right text-sm font-semibold text-foreground">SPF % <span className="text-xs font-normal text-muted-foreground">(Omani only)</span></th>
            <th className="px-4 py-3 text-right text-sm font-semibold text-foreground">SPF Value</th>
            <th className="px-4 py-3 text-center text-sm font-semibold text-foreground">Health Ins.</th>
            <th className="px-4 py-3 text-right text-sm font-semibold text-foreground">Health Ins. Value</th>
            <th className="px-4 py-3 text-right text-sm font-semibold text-foreground">Utilization %</th>
            <th className="w-32 px-4 py-3 text-center text-sm font-semibold text-foreground">Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredData?.map((item) => {
            const dailySalary = item?.dailySalary ?? calcDailySalary(item?.monthlySalary);
            const hourlySalary = item?.hourlySalary ?? calcHourlySalary(dailySalary);
            const nationality = item?.nationality || 'expat';
            const spfPercentage = item?.spfPercentage ?? DEFAULT_SPF_PERCENTAGE;
            const spfValue = item?.spfValue ?? calcSPFValue(item?.monthlySalary, spfPercentage, nationality);
            const healthInsuranceEnabled = item?.healthInsuranceEnabled ?? false;
            const healthInsuranceValue = item?.healthInsuranceValue ?? 0;

            const editDailySalary = editData?.dailySalary ?? calcDailySalary(editData?.monthlySalary);
            const editHourlySalary = editData?.hourlySalary ?? calcHourlySalary(editDailySalary);
            const editNationality = editData?.nationality || 'expat';
            const editSpfValue = editData?.spfValue ?? calcSPFValue(editData?.monthlySalary, editData?.spfPercentage ?? DEFAULT_SPF_PERCENTAGE, editNationality);

            return (
              <tr key={item?.id} className="border-b border-border hover:bg-muted/50 transition-smooth">
                <td className="px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selectedRows?.includes(item?.id)}
                    onChange={() => handleSelectRow(item?.id)}
                    className="w-4 h-4 rounded border-border"
                  />
                </td>
                {/* Name */}
                <td className="px-4 py-3">
                  {editingId === item?.id ? (
                    <Input
                      type="text"
                      value={editData?.name ?? ''}
                      onChange={(e) => setEditData({ ...editData, name: e?.target?.value })}
                      className="w-full"
                    />
                  ) : (
                    <span className="text-sm text-foreground font-medium">{item?.name}</span>
                  )}
                </td>
                {/* Role */}
                <td className="px-4 py-3">
                  {editingId === item?.id ? (
                    <Select
                      options={roleOptions}
                      value={editData?.role}
                      onChange={(value) => setEditData({ ...editData, role: value })}
                    />
                  ) : (
                    <span className="text-sm text-muted-foreground capitalize">{item?.role}</span>
                  )}
                </td>
                {/* Specialization */}
                <td className="px-4 py-3">
                  {editingId === item?.id ? (
                    <Input
                      type="text"
                      value={editData?.specialization ?? ''}
                      onChange={(e) => setEditData({ ...editData, specialization: e?.target?.value })}
                      className="w-full"
                    />
                  ) : (
                    <span className="text-sm text-muted-foreground">{item?.specialization}</span>
                  )}
                </td>
                {/* Nationality */}
                <td className="px-4 py-3">
                  {editingId === item?.id ? (
                    <Select
                      options={nationalityOptions}
                      value={editData?.nationality || 'expat'}
                      onChange={handleNationalityChange}
                    />
                  ) : (
                    <span className={`text-sm font-medium ${nationality === 'omani' ? 'text-green-600' : 'text-muted-foreground'}`}>
                      {nationality === 'omani' ? 'Omani' : 'Expat'}
                    </span>
                  )}
                </td>
                {/* Monthly Salary */}
                <td className="px-4 py-3 text-right">
                  {editingId === item?.id ? (
                    <Input
                      type="number"
                      value={editData?.monthlySalary ?? ''}
                      onChange={(e) => handleMonthlySalaryChange(e?.target?.value)}
                      className="w-full text-right"
                      placeholder="0"
                    />
                  ) : (
                    <span className="text-sm text-foreground font-medium">{formatCurrency(item?.monthlySalary)}</span>
                  )}
                </td>
                {/* Daily Salary (auto-calculated: Monthly / 30) */}
                <td className="px-4 py-3 text-right">
                  <span className="text-sm text-primary font-medium">
                    {editingId === item?.id ? formatCurrency(editDailySalary) : formatCurrency(dailySalary)}
                  </span>
                </td>
                {/* Hourly Salary (auto-calculated: Daily / 9) */}
                <td className="px-4 py-3 text-right">
                  <span className="text-sm text-accent font-medium">
                    {editingId === item?.id ? formatCurrency(editHourlySalary) : formatCurrency(hourlySalary)}
                  </span>
                </td>
                {/* SPF % */}
                <td className="px-4 py-3 text-right">
                  {editingId === item?.id ? (
                    editNationality === 'omani' ? (
                      <Input
                        type="number"
                        value={editData?.spfPercentage ?? DEFAULT_SPF_PERCENTAGE}
                        onChange={(e) => handleSPFPercentageChange(e?.target?.value)}
                        className="w-full text-right"
                        placeholder="22.5"
                      />
                    ) : (
                      <span className="text-sm text-muted-foreground">—</span>
                    )
                  ) : (
                    nationality === 'omani' ? (
                      <span className="text-sm text-foreground">{spfPercentage}%</span>
                    ) : (
                      <span className="text-sm text-muted-foreground">—</span>
                    )
                  )}
                </td>
                {/* SPF Value */}
                <td className="px-4 py-3 text-right">
                  {editingId === item?.id ? (
                    editNationality === 'omani' ? (
                      <span className="text-sm text-orange-600 font-medium">{formatCurrency(editSpfValue)}</span>
                    ) : (
                      <span className="text-sm text-muted-foreground">—</span>
                    )
                  ) : (
                    nationality === 'omani' ? (
                      <span className="text-sm text-orange-600 font-medium">{formatCurrency(spfValue)}</span>
                    ) : (
                      <span className="text-sm text-muted-foreground">—</span>
                    )
                  )}
                </td>
                {/* Health Insurance Toggle */}
                <td className="px-4 py-3 text-center">
                  {editingId === item?.id ? (
                    <input
                      type="checkbox"
                      checked={editData?.healthInsuranceEnabled ?? false}
                      onChange={(e) => setEditData({ ...editData, healthInsuranceEnabled: e?.target?.checked })}
                      className="w-4 h-4 rounded border-border accent-primary"
                    />
                  ) : (
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${healthInsuranceEnabled ? 'bg-green-100 text-green-700' : 'bg-muted text-muted-foreground'}`}>
                      {healthInsuranceEnabled ? 'Yes' : 'No'}
                    </span>
                  )}
                </td>
                {/* Health Insurance Value */}
                <td className="px-4 py-3 text-right">
                  {editingId === item?.id ? (
                    editData?.healthInsuranceEnabled ? (
                      <Input
                        type="number"
                        value={editData?.healthInsuranceValue ?? ''}
                        onChange={(e) => setEditData({ ...editData, healthInsuranceValue: parseFloat(e?.target?.value) || 0 })}
                        className="w-full text-right"
                        placeholder="0"
                      />
                    ) : (
                      <span className="text-sm text-muted-foreground">—</span>
                    )
                  ) : (
                    healthInsuranceEnabled ? (
                      <span className="text-sm text-blue-600 font-medium">{formatCurrency(healthInsuranceValue)}</span>
                    ) : (
                      <span className="text-sm text-muted-foreground">—</span>
                    )
                  )}
                </td>
                {/* Utilization % */}
                <td className="px-4 py-3 text-right">
                  {editingId === item?.id ? (
                    <Input
                      type="number"
                      value={editData?.utilizationDefault ?? ''}
                      onChange={(e) => setEditData({ ...editData, utilizationDefault: parseFloat(e?.target?.value) || 0 })}
                      className="w-full text-right"
                    />
                  ) : (
                    <span className="text-sm text-foreground">{item?.utilizationDefault}%</span>
                  )}
                </td>
                {/* Actions */}
                <td className="px-4 py-3">
                  <div className="flex items-center justify-center gap-2">
                    {editingId === item?.id ? (
                      <>
                        <Button variant="success" size="xs" iconName="Check" onClick={handleSave} />
                        <Button variant="ghost" size="xs" iconName="X" onClick={handleCancel} />
                      </>
                    ) : (
                      <>
                        <Button variant="ghost" size="xs" iconName="Edit" onClick={() => handleEdit(item)} />
                        <Button variant="ghost" size="xs" iconName="Trash2" onClick={() => onDelete(item?.id)} />
                      </>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
          {filteredData?.length === 0 && (
            <tr>
              <td colSpan={14} className="px-4 py-12 text-center text-sm text-muted-foreground">
                No engineers found. Add your first engineer using the "Add New" button.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default EngineersGrid;